/**
 * useCycle — universal auto-rotate hook for any list-bearing primitive.
 *
 * Tied to workspace view_preferences.rotation_enabled: when the AI
 * flips that flag off via set_view_preferences, EVERY primitive using
 * useCycle pauses globally without re-composing.
 *
 * Implementation note: parents re-render frequently (workspace switch,
 * cohort fetch, prefs fetch) and a naive useEffect with deps would
 * tear down + re-create the interval on every re-render before it had
 * a chance to fire. We use a ref to hold the latest config and run
 * the interval setup ONCE on mount; the tick callback reads the ref
 * for current values. This way config changes (interval_ms, paused,
 * etc.) take effect on the next tick without losing the running
 * interval.
 */

import { useEffect, useRef, useState } from "react";
import { useViewPreferences } from "./EntityRef.js";

export interface UseCycleOptions {
  enabled?: boolean;     // local opt-in (separate from prefs flag)
  interval_ms?: number;  // default 4000
  paused?: boolean;      // local pause (e.g. on hover)
  workspace_id?: string | null;
}

export function useCycle(length: number, options: UseCycleOptions = {}): {
  index: number;
  setIndex: (i: number) => void;
} {
  const { enabled = true, interval_ms = 4000, paused = false, workspace_id } = options;
  const [index, setIndex] = useState(0);

  const { data: prefsData } = useViewPreferences(workspace_id ?? null);
  const rotationEnabled = prefsData?.prefs?.rotation_enabled ?? true;

  // Hold the latest config in a ref so the running interval reads
  // current values without needing to re-create itself.
  const cfgRef = useRef({ enabled, paused, rotationEnabled, interval_ms, length });
  cfgRef.current = { enabled, paused, rotationEnabled, interval_ms, length };

  useEffect(() => {
    const id = setInterval(() => {
      const c = cfgRef.current;
      if (!c.enabled || c.paused || !c.rotationEnabled || c.length <= 1) return;
      setIndex((i) => (i + 1) % c.length);
    }, Math.max(300, interval_ms));
    return () => clearInterval(id);
    // Setup runs once on mount. interval_ms only sets the BASE tick
    // rate (the body re-checks all gates each tick). To change rate,
    // unmount + remount (or use a key). Length changes are read live.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset index if list shrank.
  useEffect(() => {
    if (index >= length) setIndex(0);
  }, [length, index]);

  return { index, setIndex };
}
